import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package,
  Plus,
  X,
  ExternalLink,
  ClipboardCheck,
  ListTodo,
  Link,
  Search,
  Loader2,
  AlertCircle,
  Check,
  Eye,
  Trash2,
  MapPin,
  User,
  Calendar,
  Building2,
  Barcode as BarcodeIcon,
  Shield,
  FileText,
  Phone,
  Mail,
  Factory,
  Hash,
  ChevronLeft,
  ChevronRight,
  XCircle,
} from 'lucide-react';
import type { Task } from '@/types/task';
import type { Asset } from '@/types/asset';
import { useToast } from '@/hooks/use-toast';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { dataService } from '@/services/data-service';
import { auditDataService } from '@/services/audit-data-service';
import { getTasks } from '@/services/tasks-service';
import { getAssetPrimaryPhotoUrl } from '@/lib/asset-utils';

interface TaskDetailLinkedEntitiesProps {
  readonly task: Task;
  readonly onTaskUpdate: (task: Task) => void;
  readonly isEditMode?: boolean;
}

type LinkedEntityType = 'asset' | 'audit' | 'task';

interface LinkedEntity {
  id: string;
  name: string;
  code?: string;
  type: LinkedEntityType;
  status?: string;
  category?: string;
  imageUrl?: string;
  linkedAt: string;
  linkedBy: string;
}

// Helper function to load asset entities - extracted to reduce cognitive complexity (S3776)
async function loadAssetEntities(isLive: boolean): Promise<any[]> {
  if (isLive) {
    const assetsResponse = await dataService.getAssets();
    return assetsResponse.data.map((asset: Asset) => ({
      id: asset.id,
      name: asset.name || 'Unnamed Asset',
      code: asset.code,
      type: 'asset' as const,
      status: asset.status,
      category: asset.category,
      imageUrl: getAssetPrimaryPhotoUrl(asset),
    }));
  }
  return generateMockAssets();
}

// Helper function to load audit entities - extracted to reduce cognitive complexity (S3776)
async function loadAuditEntities(isLive: boolean): Promise<any[]> {
  if (isLive) {
    const auditsResponse = await auditDataService.getAudits();
    return auditsResponse.items.map(audit => ({
      id: audit.id.toString(),
      name: audit.name ?? 'Unnamed Audit',
      code: audit.auditNumber ?? audit.id.toString(),
      type: 'audit' as const,
      status: audit.status,
      category: audit.type,
    }));
  }
  return generateMockAudits();
}

// Helper function to load task entities - extracted to reduce cognitive complexity (S3776)
async function loadTaskEntities(isLive: boolean): Promise<any[]> {
  if (isLive) {
    const tasksResponse = await getTasks();
    return tasksResponse.data.map(task => ({
      id: task.id,
      name: task.title || 'Unnamed Work Item',
      code: task.id,
      type: 'task' as const,
      status: task.status,
      category: task.type,
    }));
  }
  return generateMockTasks();
}

// Helper function to get mock data by type - extracted to reduce cognitive complexity (S3776)
function getMockDataByType(selectedType: 'asset' | 'audit' | 'task'): any[] {
  switch (selectedType) {
    case 'asset':
      return generateMockAssets();
    case 'audit':
      return generateMockAudits();
    case 'task':
      return generateMockTasks();
  }
}

// Mock data generators for demo mode
const generateMockAssets = (count: number = 150) => {
  const statuses = ['Active', 'Inactive', 'Maintenance', 'Retired'];
  const categories = ['Equipment', 'IT Equipment', 'Vehicle', 'Machinery', 'Tools', 'Furniture'];
  const prefixes = ['FL', 'HP', 'VH', 'GN', 'MC', 'TL', 'FR'];
  
  return Array.from({ length: count }, (_, i) => ({
    id: `asset-${i + 1}`,
    name: `Asset ${prefixes[i % prefixes.length]}-${String(i + 1).padStart(3, '0')}`,
    code: `${prefixes[i % prefixes.length]}-${String(i + 1).padStart(3, '0')}`,
    type: 'asset' as const,
    status: statuses[i % statuses.length],
    category: categories[i % categories.length],
    imageUrl: undefined,
  }));
};

const generateMockAudits = (count: number = 80) => {
  const statuses = ['In Progress', 'Completed', 'Scheduled', 'Cancelled'];
  const types = ['Asset Audit', 'Safety Check', 'Compliance Review', 'Quality Inspection'];
  
  return Array.from({ length: count }, (_, i) => ({
    id: `audit-${i + 1}`,
    name: `${types[i % types.length]} ${i + 1}`,
    code: `AUD-2024-${String(i + 1).padStart(3, '0')}`,
    type: 'audit' as const,
    status: statuses[i % statuses.length],
  }));
};

const generateMockTasks = (count: number = 120) => {
  const statuses = ['New', 'In Progress', 'On Hold', 'Completed'];
  const types = ['Maintenance', 'Inspection', 'Repair', 'Installation', 'Review'];
  
  return Array.from({ length: count }, (_, i) => ({
    id: `task-${i + 1}`,
    name: `${types[i % types.length]} Work Item ${i + 1}`,
    code: `TSK-${String(i + 1).padStart(3, '0')}`,
    type: 'task' as const,
    status: statuses[i % statuses.length],
  }));
};

export function TaskDetailLinkedEntities({ task, onTaskUpdate, isEditMode = false }: TaskDetailLinkedEntitiesProps) {
  const { toast } = useToast();
  const { modules } = useFeatureSettings();
  
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState<LinkedEntity | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState<LinkedEntity | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState<{ entity: LinkedEntity; action: 'navigate' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<'asset' | 'audit' | 'task'>('asset');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [hoveredEntity, setHoveredEntity] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [availableEntities, setAvailableEntities] = useState<any[]>([]);
  const [entityDetails, setEntityDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const itemsPerPage = 20;

  // Mock linked entities - in real app, this would come from task.linkedEntities
  const [linkedEntities, setLinkedEntities] = useState<LinkedEntity[]>([
    {
      id: 'link-1',
      name: 'Forklift FL-001',
      code: 'FL-001',
      type: 'asset',
      status: 'Active',
      category: 'Equipment',
      linkedAt: '2024-01-15T10:30:00Z',
      linkedBy: 'John Smith',
    },
  ]);

  // Load available entities from API or mock data
  useEffect(() => {
    loadAvailableEntities();
  }, [selectedType, searchQuery]);

  // Reset to page 1 when search or type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, searchQuery]);

  // Reset selection when dialog closes
  useEffect(() => {
    if (!showLinkDialog) {
      setSelectedItems(new Set());
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, [showLinkDialog]);

  // Load available entities using extracted helper functions to reduce cognitive complexity (S3776)
  const loadAvailableEntities = async () => {
    setIsLoading(true);
    try {
      // Check if module is in live mode
      const isAssetsLive = modules.assets?.mode === 'live';
      const isAuditsLive = modules.audits?.mode === 'live';
      const isTasksLive = modules.workItems?.mode === 'live';

      // Load entities based on selected type using extracted helper functions
      let entities: any[] = [];
      switch (selectedType) {
        case 'asset':
          entities = await loadAssetEntities(isAssetsLive);
          break;
        case 'audit':
          entities = await loadAuditEntities(isAuditsLive);
          break;
        case 'task':
          entities = await loadTaskEntities(isTasksLive);
          break;
      }

      // Filter by search query
      if (searchQuery) {
        entities = entities.filter(e => 
          e.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          e.code?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setAvailableEntities(entities);
    } catch (error) {
      console.error('Error loading entities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load entities. Please try again.',
        variant: 'destructive',
      });
      // Fallback to mock data on error using extracted helper function
      setAvailableEntities(getMockDataByType(selectedType));
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate pagination
  const totalItems = availableEntities.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEntities = availableEntities.slice(startIndex, endIndex);

  // Get linked entity IDs for the current type
  const linkedEntityIds = useMemo(() => {
    return new Set(linkedEntities.filter(e => e.type === selectedType).map(e => e.id));
  }, [linkedEntities, selectedType]);

  const handleToggleItem = (entityId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entityId)) {
        newSet.delete(entityId);
      } else {
        newSet.add(entityId);
      }
      return newSet;
    });
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleLinkEntities = () => {
    if (selectedItems.size === 0) return;

    // Convert selected IDs to full entities
    const entitiesToLink = availableEntities
      .filter(e => selectedItems.has(e.id))
      .map(e => ({
        ...e,
        linkedAt: new Date().toISOString(),
        linkedBy: 'Current User',
      }));

    // TODO: API call to save links to backend
    // await dataService.addLinkedEntities(task.id, Array.from(selectedItems), selectedType);

    setLinkedEntities(prev => [...prev, ...entitiesToLink]);
    setShowLinkDialog(false);

    toast({
      title: 'Entities Linked',
      description: `${selectedItems.size} ${selectedType}${selectedItems.size > 1 ? 's' : ''} linked successfully.`,
    });
  };

  const handleRemoveEntity = () => {
    if (!showRemoveDialog) return;

    setLinkedEntities(prev => prev.filter(e => e.id !== showRemoveDialog.id));
    setShowRemoveDialog(null);

    toast({
      title: 'Link Removed',
      description: `${showRemoveDialog.name} has been removed from this task.`,
    });
  };

  const fetchEntityDetails = async (entity: LinkedEntity) => {
    setLoadingDetails(true);
    setEntityDetails(null);
    
    try {
      const isTasksLive = modules.workItems?.mode === 'live';
      const isAuditsLive = modules.audits?.mode === 'live';
      
      switch (entity.type) {
        case 'task':
          if (isTasksLive) {
            const response = await getTasks();
            const taskData = response.data.find(t => t.id === entity.id);
            if (taskData) {
              setEntityDetails(taskData);
            }
          }
          break;
        case 'audit':
          if (isAuditsLive) {
            const response = await auditDataService.getAudits();
            const auditData = response.items.find(a => a.id.toString() === entity.id);
            if (auditData) {
              setEntityDetails(auditData);
            }
          }
          break;
        case 'asset':
          // Asset details would be fetched here if needed
          break;
      }
    } catch (error) {
      console.error('Error fetching entity details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleEntityClick = (entity: LinkedEntity) => {
    setShowDetailSheet(entity);
    fetchEntityDetails(entity);
  };

  const handleOpenEntity = (entity: LinkedEntity) => {
    // Check if in edit mode and has unsaved changes
    if (isEditMode) {
      setShowUnsavedDialog({ entity, action: 'navigate' });
    } else {
      // Navigate directly
      navigateToEntity(entity);
    }
  };

  const navigateToEntity = (entity: LinkedEntity) => {
    const routes = {
      asset: `/assets/${entity.id}`,
      audit: `/asset-audits/${entity.id}`,
      task: `/tasks/${entity.id}`,
    };
    window.location.href = routes[entity.type];
  };

  const handleContinueNavigation = () => {
    if (showUnsavedDialog) {
      navigateToEntity(showUnsavedDialog.entity);
      setShowUnsavedDialog(null);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'new':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEntityIcon = (type: string, className: string = "h-3.5 w-3.5") => {
    switch (type) {
      case 'asset':
        return <Package className={`${className} text-muted-foreground`} />;
      case 'audit':
        return <ClipboardCheck className={`${className} text-muted-foreground`} />;
      case 'task':
        return <ListTodo className={`${className} text-muted-foreground`} />;
      default:
        return null;
    }
  };

  const getEntityTypeLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Group entities by type
  const groupedEntities = linkedEntities.reduce((acc, entity) => {
    if (!acc[entity.type]) {
      acc[entity.type] = [];
    }
    acc[entity.type].push(entity);
    return acc;
  }, {} as Record<string, LinkedEntity[]>);

  return (
    <>
      <Card className="border border-border/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Link className="h-4 w-4" />
                Linked Entities
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Related assets, audits, and tasks
              </CardDescription>
            </div>
            {isEditMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLinkDialog(true)}
                className="h-8 px-3 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Link
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.keys(groupedEntities).length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No linked entities
            </div>
          ) : (
            <>
              {Object.entries(groupedEntities).map(([type, entities]) => (
                <div key={type} className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    {getEntityIcon(type)}
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {getEntityTypeLabel(type)}s
                    </span>
                  </div>
                  <div className="space-y-1">
                    {entities.map((entity) => (
                      <div
                        key={entity.id}
                        className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 transition-colors group"
                        onMouseEnter={() => setHoveredEntity(entity.id)}
                        onMouseLeave={() => setHoveredEntity(null)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{entity.name}</div>
                          <div className="text-xs text-muted-foreground">{entity.code || entity.id}</div>
                        </div>
                        <div className={`flex items-center gap-0.5 transition-opacity ${
                          hoveredEntity === entity.id ? 'opacity-100' : 'opacity-0'
                        }`}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleEntityClick(entity)}
                            title="View details"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          {isEditMode && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-destructive"
                              onClick={() => setShowRemoveDialog(entity)}
                              title="Remove link"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* Link Entity Dialog - Redesigned for Large Datasets */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] bg-white dark:bg-background flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>Link Entities</DialogTitle>
            <DialogDescription>
              Search and select items to link to this task
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Tabs for Entity Types */}
            <div className="px-6 pt-4">
              <Tabs value={selectedType} onValueChange={(value: any) => setSelectedType(value)}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="asset" className="gap-2">
                    <Package className="h-4 w-4" />
                    Assets
                  </TabsTrigger>
                  <TabsTrigger value="audit" className="gap-2">
                    <ClipboardCheck className="h-4 w-4" />
                    Audits
                  </TabsTrigger>
                  <TabsTrigger value="task" className="gap-2">
                    <ListTodo className="h-4 w-4" />
                    Work Items
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Search Bar */}
            <div className="px-6 py-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={`Search ${selectedType}s by name or code...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Results Summary */}
              <div className="flex items-center justify-between text-sm">
                <div className="text-muted-foreground">
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    <span>
                      Showing {Math.min(startIndex + 1, totalItems)}-{Math.min(endIndex, totalItems)} of {totalItems} {selectedType}s
                      {selectedItems.size > 0 && (
                        <span className="ml-2">
                          • <strong>{selectedItems.size}</strong> selected
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {selectedItems.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSelection}
                    className="h-7 text-xs"
                  >
                    Clear selection
                  </Button>
                )}
              </div>
            </div>

            {/* Entity List */}
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : paginatedEntities.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No {selectedType}s found</p>
                  {searchQuery && (
                    <p className="text-sm mt-1">Try adjusting your search terms</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                    {paginatedEntities.map((entity: any) => {
                      const isSelected = selectedItems.has(entity.id);
                      const isAlreadyLinked = linkedEntityIds.has(entity.id);

                      const getButtonClassName = () => {
                      if (isAlreadyLinked) return 'opacity-50 cursor-not-allowed bg-muted/50 border-muted';
                      if (isSelected) return 'border-primary bg-primary/5 hover:bg-primary/10';
                      return 'border-border hover:border-primary/50 hover:bg-accent/50';
                    };

                    return (
                      <button
                        key={entity.id}
                        onClick={() => !isAlreadyLinked && handleToggleItem(entity.id)}
                        disabled={isAlreadyLinked}
                        className={`w-full p-2 border rounded-lg text-left transition-all ${getButtonClassName()}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="flex items-center justify-center flex-shrink-0">
                            {isAlreadyLinked ? (
                              <div className="w-4 h-4 rounded border-2 border-muted-foreground bg-muted flex items-center justify-center">
                                <Check className="h-2.5 w-2.5 text-muted-foreground" />
                              </div>
                            ) : (
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleToggleItem(entity.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                              />
                            )}
                          </div>
                          <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                            {entity.imageUrl ? (
                              <img
                                src={entity.imageUrl}
                                alt={entity.name}
                                className="w-full h-full object-cover rounded-md"
                              />
                            ) : (
                              <div className="text-muted-foreground">
                                {getEntityIcon(entity.type, 'h-5 w-5')}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h4 className="font-medium text-sm truncate">
                                {entity.name}
                              </h4>
                              {isAlreadyLinked && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                  Linked
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[11px] text-muted-foreground font-mono">
                                {entity.code || entity.id}
                              </span>
                              {entity.status && (
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatusColor(entity.status)}`}>
                                  {entity.status}
                                </Badge>
                              )}
                              {entity.category && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                  {entity.category}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {!isLoading && totalPages > 1 && (
              <div className="px-6 py-4 border-t bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-background">
            <Button
              variant="outline"
              onClick={() => setShowLinkDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkEntities}
              disabled={selectedItems.size === 0 || isLoading}
            >
              {selectedItems.size > 0 ? `Add (${selectedItems.size})` : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entity Detail Sheet */}
      <Sheet open={!!showDetailSheet} onOpenChange={(open) => !open && setShowDetailSheet(null)}>
        <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col">
          {showDetailSheet && (
            <>
              {showDetailSheet.type === 'asset' ? (
                // Asset-specific layout with hero header at top
                <>
                  {/* Hero Header with Image Overlay */}
                  <div className="relative h-56 overflow-hidden flex-shrink-0">
                    {showDetailSheet.imageUrl ? (
                      <>
                        <img
                          src={showDetailSheet.imageUrl}
                          alt={showDetailSheet.name}
                          className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                      </>
                    ) : (
                      <>
                        <div className="h-full w-full bg-gradient-to-br from-blue-600 to-blue-800" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
                      </>
                    )}
                    
                    {/* Close button - visible on all backgrounds */}
                    <button
                      onClick={() => setShowDetailSheet(null)}
                      className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>

                    {/* Asset name and badges */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-2 mb-3">
                        {showDetailSheet.status && (
                          <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                            {showDetailSheet.status}
                          </Badge>
                        )}
                        {showDetailSheet.category && (
                          <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                            {showDetailSheet.category}
                          </Badge>
                        )}
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-1">
                        {showDetailSheet.name}
                      </h2>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                    {/* Primary Information */}
                    <div className="space-y-4">
                      {/* Code */}
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <Hash className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">Code</p>
                          <p className="font-semibold">{showDetailSheet.code || 'AST-2024-001'}</p>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">Description</p>
                          <p className="text-sm leading-relaxed">
                            High-performance industrial equipment designed for heavy-duty operations. Features advanced monitoring capabilities and energy-efficient operation.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Location & Assignment */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">Location</p>
                          <p className="font-semibold text-sm">Building A</p>
                          <p className="text-xs text-muted-foreground">Floor 3, Room 305</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">Assigned To</p>
                          <p className="font-semibold text-sm">John Smith</p>
                          <p className="text-xs text-muted-foreground">Operations Team</p>
                        </div>
                      </div>
                    </div>

                    {/* Date Acquired */}
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-muted/50">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-0.5">Date Acquired</p>
                        <p className="font-semibold">January 15, 2023</p>
                        <p className="text-xs text-muted-foreground">1 year, 11 months old</p>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Technical Specifications */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Technical Specifications
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Manufacturer */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted/50">
                            <Factory className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">Manufacturer</p>
                            <p className="font-semibold text-sm">Acme Industries</p>
                          </div>
                        </div>

                        {/* Model */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted/50">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">Model</p>
                            <p className="font-semibold text-sm">X-5000 Pro</p>
                          </div>
                        </div>
                      </div>

                      {/* Serial Number */}
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <Hash className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">Serial Number</p>
                          <p className="font-mono font-semibold text-sm">SN-2023-X5000-00142</p>
                        </div>
                      </div>

                      {/* Barcode */}
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted/50">
                          <BarcodeIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">Barcode</p>
                          <p className="font-mono font-semibold text-sm">7891234567890</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Insurance & Warranty */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Insurance & Warranty
                      </h3>

                      <div className="space-y-3">
                        {/* Insurance */}
                        <div className="p-4 rounded-lg bg-muted/30 border">
                          <div className="flex items-start gap-3 mb-3">
                            <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-sm mb-1">Insurance Coverage</p>
                              <p className="text-xs text-muted-foreground">Policy #: INS-2023-45678</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground">Provider</p>
                              <p className="font-medium">SafeGuard Insurance Co.</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Expires</p>
                              <p className="font-medium">Dec 31, 2024</p>
                            </div>
                          </div>
                        </div>

                        {/* Warranty */}
                        <div className="p-4 rounded-lg bg-muted/30 border">
                          <div className="flex items-start gap-3 mb-3">
                            <Shield className="h-4 w-4 text-green-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-sm mb-1">Manufacturer Warranty</p>
                              <p className="text-xs text-muted-foreground">3-Year Extended Coverage</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground">Valid Until</p>
                              <p className="font-medium">Jan 15, 2026</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Status</p>
                              <div className="flex items-center gap-1">
                                <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                <p className="font-medium">Active</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t" />

                    {/* Supplier Information */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Supplier Information
                      </h3>

                      <div className="p-4 rounded-lg bg-muted/30 border space-y-3">
                        <div>
                          <p className="font-semibold mb-1">TechSupply Solutions Ltd.</p>
                          <p className="text-xs text-muted-foreground">Authorized Distributor</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                            <a href="tel:+1234567890" className="hover:underline">
                              +1 (234) 567-890
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            <a href="mailto:support@techsupply.com" className="hover:underline">
                              support@techsupply.com
                            </a>
                          </div>
                        </div>

                        <div className="pt-2 border-t text-xs">
                          <p className="text-muted-foreground mb-1">Account Manager</p>
                          <p className="font-medium">Sarah Johnson</p>
                        </div>
                      </div>
                    </div>
                  </div>

                                  {/* Fixed Footer with Open Button */}
                                  <div className="flex-shrink-0 border-t bg-background p-4">
                                    <Button
                                      onClick={() => handleOpenEntity(showDetailSheet)}
                                      className="w-full"
                                      size="lg"
                                    >
                                      <ExternalLink className="h-4 w-4 mr-2" />
                                      Open Asset
                                    </Button>
                                  </div>
                                </>
                                ) : (
                  // Enhanced layout for audits and tasks (no image header)
                  <>
                    {/* Header with Icon and Title */}
                    <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-primary/10 flex-shrink-0">
                          {showDetailSheet.type === 'audit' ? (
                            <ClipboardCheck className="h-6 w-6 text-primary" />
                          ) : (
                            <ListTodo className="h-6 w-6 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {showDetailSheet.status && (
                              <Badge variant="outline" className={getStatusColor(showDetailSheet.status)}>
                                {showDetailSheet.status}
                              </Badge>
                            )}
                            {showDetailSheet.category && (
                              <Badge variant="secondary">
                                {showDetailSheet.category}
                              </Badge>
                            )}
                          </div>
                          <h2 className="text-xl font-bold mb-1">
                            {showDetailSheet.name}
                          </h2>
                          <p className="text-sm text-muted-foreground">
                            {getEntityTypeLabel(showDetailSheet.type)} Details
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                      {/* Primary Information */}
                      <div className="space-y-4">
                        {/* Code/ID */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted/50">
                            <Hash className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">
                              {showDetailSheet.type === 'audit' ? 'Audit Code' : 'Work Item Code'}
                            </p>
                            <p className="font-mono font-semibold text-sm">{showDetailSheet.code || showDetailSheet.id}</p>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted/50">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">Description</p>
                            <p className="text-sm leading-relaxed">
                              {(() => {
                                if (loadingDetails) return <span className="text-muted-foreground">Loading...</span>;
                                if (entityDetails?.description) return entityDetails.description;
                                if (showDetailSheet.type === 'audit') return 'Comprehensive audit to verify asset records, locations, and conditions. Includes physical verification and documentation review.';
                                return 'Work item created to address specific requirements and deliverables. Includes detailed steps and acceptance criteria.';
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t" />

                      {/* Audit-specific or Work Item-specific Details */}
                      {showDetailSheet.type === 'audit' ? (
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                            Audit Information
                          </h3>

                          <div className="grid grid-cols-2 gap-4">
                            {/* Audit Type */}
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted/50">
                                <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground mb-0.5">Type</p>
                                <p className="font-semibold text-sm">
                                  {loadingDetails ? '...' : entityDetails?.type || showDetailSheet.category || 'Asset Audit'}
                                </p>
                              </div>
                            </div>

                            {/* Scheduled Date */}
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted/50">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground mb-0.5">Scheduled</p>
                                <p className="font-semibold text-sm">
                                  {(() => {
                                    if (loadingDetails) return '...';
                                    if (entityDetails?.scheduledDate) {
                                      return new Date(entityDetails.scheduledDate).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      });
                                    }
                                    return 'Not scheduled';
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Auditor */}
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-muted/50">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground mb-0.5">Lead Auditor</p>
                              <p className="font-semibold text-sm">
                                {loadingDetails ? '...' : entityDetails?.leadAuditor || 'Not assigned'}
                              </p>
                              {entityDetails?.auditorRole && (
                                <p className="text-xs text-muted-foreground">{entityDetails.auditorRole}</p>
                              )}
                            </div>
                          </div>

                          {/* Location */}
                          {(entityDetails?.location || entityDetails?.subLocation) && (
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted/50">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground mb-0.5">Location</p>
                                <p className="font-semibold text-sm">{entityDetails?.location || 'Not specified'}</p>
                                {entityDetails?.subLocation && (
                                  <p className="text-xs text-muted-foreground">{entityDetails.subLocation}</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Progress */}
                          <div className="p-4 rounded-lg bg-muted/30 border">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Progress</p>
                              <p className="text-sm font-bold">65%</p>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: '65%' }} />
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                              <div>
                                <p className="text-muted-foreground">Verified</p>
                                <p className="font-semibold">156</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Pending</p>
                                <p className="font-semibold">84</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Issues</p>
                                <p className="font-semibold text-destructive">3</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                            Work Item Information
                          </h3>

                          <div className="grid grid-cols-2 gap-4">
                            {/* Work Item Type */}
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted/50">
                                <ListTodo className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground mb-0.5">Type</p>
                                <p className="font-semibold text-sm">
                                  {loadingDetails ? '...' : entityDetails?.type || showDetailSheet.category || 'General Task'}
                                </p>
                              </div>
                            </div>

                            {/* Due Date */}
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted/50">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground mb-0.5">Due Date</p>
                                <p className="font-semibold text-sm">
                                  {(() => {
                                    if (loadingDetails) return '...';
                                    if (entityDetails?.dueDate) {
                                      return new Date(entityDetails.dueDate).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      });
                                    }
                                    return 'Not set';
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Assignee */}
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-muted/50">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground mb-0.5">Assigned To</p>
                              <p className="font-semibold text-sm">
                                {loadingDetails ? '...' : entityDetails?.assignedTo || 'Unassigned'}
                              </p>
                              {entityDetails?.team && (
                                <p className="text-xs text-muted-foreground">{entityDetails.team}</p>
                              )}
                            </div>
                          </div>

                          {/* Priority */}
                          {entityDetails?.priority && (
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-muted/50">
                                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground mb-0.5">Priority</p>
                                <div className="flex items-center gap-2">
                                  <div className={`h-2 w-2 rounded-full ${
                                    (() => {
                                      if (entityDetails.priority === 'High') return 'bg-red-500';
                                      if (entityDetails.priority === 'Medium') return 'bg-orange-500';
                                      return 'bg-blue-500';
                                    })()
                                  }`} />
                                  <p className="font-semibold text-sm">{entityDetails.priority}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Time Tracking */}
                          {(entityDetails?.estimatedHours || entityDetails?.loggedHours) && (
                            <div className="p-4 rounded-lg bg-muted/30 border space-y-3">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time Tracking</p>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <p className="text-muted-foreground">Estimated</p>
                                  <p className="font-semibold">{entityDetails?.estimatedHours ? `${entityDetails.estimatedHours}h` : 'Not set'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Logged</p>
                                  <p className="font-semibold">{entityDetails?.loggedHours ? `${entityDetails.loggedHours}h` : '0h'}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="border-t" />

                      {/* Link Information */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                          Link Information
                        </h3>
                        
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted/50">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">Linked By</p>
                            <p className="font-semibold text-sm">{showDetailSheet.linkedBy}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-muted/50">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground mb-0.5">Linked Date</p>
                            <p className="font-semibold text-sm">
                              {new Date(showDetailSheet.linkedAt).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(showDetailSheet.linkedAt).toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                              {/* Fixed Footer with Open Button */}
                              <div className="flex-shrink-0 border-t bg-background p-4">
                                <Button
                                  onClick={() => handleOpenEntity(showDetailSheet)}
                                  className="w-full"
                                  size="lg"
                                >
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Open {getEntityTypeLabel(showDetailSheet.type)}
                                </Button>
                              </div>
                            </>
                          )}
                      </>
                    )}
        </SheetContent>
      </Sheet>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!showRemoveDialog} onOpenChange={(open) => !open && setShowRemoveDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Link?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Are you sure you want to remove this link? This action cannot be undone.</p>
                <span className="block mt-2 font-semibold text-foreground">{showRemoveDialog?.name}</span>
                {showRemoveDialog?.code && (
                  <span className="block text-muted-foreground">{showRemoveDialog.code}</span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveEntity}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={!!showUnsavedDialog} onOpenChange={(open) => !open && setShowUnsavedDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Opening this item will navigate away from this page and your changes will be lost. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on Page</AlertDialogCancel>
            <AlertDialogAction onClick={handleContinueNavigation}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
